/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {cloneDeep} from 'lodash';
import isArray from 'lodash.isarray';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {CVSAssetType} from '@ion/core/src/types/entities';
import GlobalGroupsSearch from '@ion/features/src/global-groups/search';
import {generateItemKey, generateKey} from '../../utils/Keys';
import {createLoaderNode, createSingleNode} from '../createTree';

export const keyIncrement = 100000;

interface InjectAssetProps {
  treeNode: any
  assets: CVSAssetType | CVSAssetType[],
  groupId: number,
  completed: boolean,
  remaining: number
};

interface AssetLoadingProps {
  rootNode: any,
  groupId: number,
  isLoading?: boolean,
  remaining?: number,
  completed?: boolean
};

interface UpdateLoaderProps {
  children: any[],
  title: string,
  loadingPending: boolean,
  isLoading: boolean,
  isLoader: boolean,
  remaining: number
};

/**
 *
 * The normalizeAccountIds method takes i/p as current global group account id's object and
 * returns the comma seperated account id string, which can be passed to CTC.
 *
 * ```typescript
 *
 * import {normalizeAccountIds} from '@ion/dls/src/selectiontree/utils';
 *
 * normalizeAccountIds({20103:true, 20104:true}); // o/p : "20103,20104"
 *
 * ```
 */
export const normalizeAccountIds = (currentGlobalGroupaccounts) => (
  Object.keys(currentGlobalGroupaccounts)
    .filter(accountId => currentGlobalGroupaccounts[accountId])
    .join(',')
);

export const convertGroupsToTree = (accounts, initialPath = '0', isRoot = false) => {
  if (!isNil(accounts) && !isEmpty(accounts)) {
    let children;
    const account = {
      ...accounts,
      key     : initialPath,
      groupId : entityId(accounts?.href),
      id      : entityId(accounts?.href)
    };
    if (accounts?.subAccounts?.length) {
      children = accounts.subAccounts.map((subAccount, index) => {
        const accountToStrip = subAccount?.subaccount ?? subAccount;
        return convertGroupsToTree(accountToStrip, `${initialPath}-${index}`, false);
      });
    }
    if (children?.length) {
      account.children = children;
    }
    return account;
  }
  return {};
};

export const createTreeKeyContentMap = (tree) => {
  if (!isNil(tree) && !isEmpty(tree)) {
    const map: {[key: string]: string} = {};
    if (tree?.children?.length) {
      tree.children.forEach((child) => {
        const childKeysMap = createTreeKeyContentMap(child);
        Object.assign(map, childKeysMap);
      });
    }
    return Object.assign(map, {[tree?.key]: entityId(tree?.href)});
  }
  return {};
};

/**
 *
 * The setAssetsLoading method updates the tree loader node
 *
 * ```typescript
 *
 * import {setAssetsLoading} from '@ion/dls/src/selectiontree/utils';
 *
 * setAssetsLoading(rootNode, groupId, remaining, completed, isLoading);
 *
 * ```
 */
export const setAssetsLoading = ({
  rootNode,
  groupId,
  remaining = 0,
  completed = false,
  isLoading = false
}: AssetLoadingProps) => {
  let found    = false;
  const locate = (node, groupId) => {
    const {props: self} = node || {};
    if (self?.id !== groupId) {
      const {children} = self || {};
      if (children?.length && !found) {
        children.forEach(account => {
          if (account && !found) {
            locate(account, groupId);
          }
        });
      }
    } else {
      if (!isLoading) {
        self.assetsLoaded = true;
        self.isLoading    = false;
        self.children     = updateLoader({
          children       : self?.children,
          title          : self?.title,
          loadingPending : false,
          isLoading      : false,
          isLoader       : true,
          remaining
        });
      } else {
        self.isLoading = true;
        self.children  = updateLoader({
          children       : self?.children,
          title          : self?.title,
          loadingPending : true,
          isLoading      : true,
          isLoader       : true,
          remaining
        });
      }
      if (completed) {
        self.children = removeLoader(self?.children);
        // Remove the expand icon if the group has no child left.
        if (!self?.children?.length) {
          self.className = 'asset';
        }
      }
      found = true;
    }
  };
  locate(rootNode, groupId);
  return found;
};

/**
 * The injectAsset function provides a utility which locates the
 * group to which an Asset resides and adds the Asset to the given
 * Group node's children.
 *
 * @method injectAsset
 * @param  {Object} treeNode The root level account from which the
 *         asset is to be located. Note, internally, this is a
 *         recursive implementation which will iterate through
 *         each node's children until the group node to which
 *         the provided Asset is to be added is located.
 * @param  {Object} assets The Assets which is to be added to the
 *         given Group.
 * @param  {number} groupId The id of the group to which the asset
 *         is to be added to it's child nodes.
 *  @param {boolean} completed indicates if all assets are loaded or not for the group
 *  @param {number} remaining indicates how many assets are remaining to load
 *
 * ```typescript
 *
 * import {injectAsset} from '@ion/dls/src/selectiontree/utils';
 *
 * const {insertedKeys, ...} = injectAsset(account, asset, groupId, completed, remaining);
 *
 * ```
 */
export const injectAsset = ({treeNode, assets, groupId, completed, remaining}: InjectAssetProps) => {
  let found          = false;
  let insertedKey    = null;
  const insertedKeys = {};
  const locateGroup = (node, assets, groupId) => {
    if (!found) {
      const {props: self} = node || {};
      found = self.id === groupId;
      if (found) {
        if (completed) {
          self.assetsLoaded = true;
        } else if (self.isLoading) {
          self.isLoading = false;
        }
        self.remaining      = remaining;
        const childrenNodes = getChildren(self?.children);
        const phantomNode   = getPhantom(self?.children);
        const loaderNode    = getLoader(self?.children);
        const children      = (self.children = childrenNodes || []);
        const items         = !isArray(assets) ? [assets] : assets;
        items?.forEach((item) => {
          if (item) {
            const {id, name, account} = item;
            // if item is asset, it has assetType property.
            const isAsset = !!item?.devices;
            let accountId;
            if (account?.title) {
              GlobalGroupsSearch.findResults(account.title);
              const res = GlobalGroupsSearch.fetchResults(1, 10);
              accountId = res[0]?.item?.accountId;
            }
            // insert item only if the accountId match with the groupId passed.
            if (Number(accountId || item.accountId) === groupId) {
              insertedKey = generateKey(children, node);
              // if inserted key is not duplicated then create a node
              if (!insertedKeys[id]) {
                insertedKeys[id] = {
                  id,
                  title    : name,
                  key      : insertedKey,
                  isDevice : !isAsset,
                  isAsset  : isAsset,
                  accountId
                };
                self.children.push(createSingleNode({
                  className : isAsset ? 'assetNode' : 'deviceNode',
                  isAsset   : isAsset,
                  isDevice  : !isAsset,
                  isLeaf    : false,
                  isLoader  : false,
                  key       : insertedKey,
                  title     : name,
                  id
                }));
              }
            }
          }
        });
        // Adding the Phantom node, this node is never removed from the tree
        self.children?.push(phantomNode);
        self.children?.push(loaderNode);
      } else if (self?.children) {
        self.children.forEach((item) => {
          if (item && !found) {
            locateGroup(item, assets, groupId);
          }
        });
      }
    }
  };
  locateGroup(treeNode, assets, groupId);
  return {
    root : treeNode,
    insertedKey,
    insertedKeys
  };
};

/**
 * removeDevicesFromTree loop through the treeNodes and remove all devices if found
 *
 * @method removeDevicesFromTree
 * @param {Object} treeNodes The root level account from which the
 *         asset and devices is to be located. Note, internally, this is a
 *         recursive implementation which will iterate through
 *         each node's children until the group node to which
 *         the provided Asset is to be added is located.
 * @param {string[] | number[]} groupIds The id of the group to which the device
 *         is to be added to it's child nodes.
 * @param {boolean} found optional
 *
 * ```typescript
 *
 * import {removeDevicesFromTree} from '@ion/dls/src/selectiontree/utils';
 *
 * const {treeNodes} = removeDevicesFromTree(nodes, groupIds);
 *
 * ```
 *
 */
export const removeDevicesFromTree = (treeNodes, groupIds: any[], found = null) => {
  const {props: self} = treeNodes || {};
  if (self) {
    const groupId = self.id ? self.id.toString() : self.groupId?.toString();
    if (groupIds.find(id => id === groupId)) {
      const filtered = self.children.filter(({props: {isDevice}}) => !isDevice);
      self.children = filtered;
      groupIds = groupIds.filter(id => id !== groupId);
    }
    const {children} = self || {};
    if (children?.length && groupIds?.length) {
      for (let i=0; i<children.length; i++) {
        const {treeNodes, updated, found} = removeDevicesFromTree(children[i], groupIds);
        if (found) {
          children[i] = treeNodes;
          groupIds = updated;
          if (!groupIds.length) {
            break;
          }
        }
      }
    }
  };
  return {treeNodes, updated: groupIds, found};
};

/**
 * Defines a utility to add the current loader node
 */
const updateLoader = ({
  children,
  title,
  loadingPending,
  isLoading,
  isLoader,
  remaining
}: UpdateLoaderProps) => {
  const childrenNodes = [];
  children?.forEach((node) => {
    if (node?.key?.includes(`-${keyIncrement}`)) {
      const {id, groupId, isAsset} = node?.props || {};
      childrenNodes.push(createLoaderNode({
        key       : node?.key,
        groupName : title,
        label     : title,
        loadingPending,
        isLoading,
        isLoader,
        remaining,
        groupId,
        isAsset,
        id
      }));
    } else {
      childrenNodes.push(node);
    }
  });
  return childrenNodes;
};

/**
 * Defines a utility to return the children nodes without phantom and loader
 */
const getChildren = (children: any[]) => {
  if (children?.length) {
    return children?.filter(({key}) => !key?.includes(`-${keyIncrement}`) && !key?.startsWith('-'));
  }
  return children;
};

/**
 * Defines a utility to return the phantom node
 */
const getPhantom = (children: any[]) => {
  return children?.find(({key}) => key?.startsWith('-'));
};

/**
 * Verifys if input node is a phantom node
 */
const isPhantomNode = (key): boolean => key.startsWith('-');

/**
 * Defines a utility to return the loader node
 */
const getLoader = (children: any[]) => {
  return children?.find(({key}) => key?.includes(`-${keyIncrement}`));
};

/**
 * Defines a utility to update the loader node for children nodes
 * to be hidden
 */
const removeLoader = (children: any[]) => {
  if (children?.length) {
    return children?.map((child) => {
      if (child?.key?.includes(`-${keyIncrement}`)) {
        const clone = cloneDeep(child);
        clone.props.className = 'loader-hidden';
        return clone;
      }
      return child;
    }
    );
  }
  return children;
};

export const createAssetParam = (item, pos: string) => ({
  className : item.isAsset ? 'assetNode' : 'deviceNode',
  key       : pos,
  ref       : null,
  isAsset   : item.isAsset,
  isDevice  : item.isDevice,
  isLeaf    : false,
  isLoader  : false,
  id        : item?.assetId ?? item?.deviceId,
  title     : item?.title
});

const locateItemIntoTree = (treeNode, item, pos = null) => {
  const {key, props} = treeNode || {};
  // run only if position is not found && all arguments are valid
  if (props && key) {
    // if current node is matching with accountId looking for, return;
    if (item?.id.toString() === props?.id?.toString()) {
      // find the loader from the existing children
      const loader = props.children.find(({props: {isLoader}}) => isLoader);
      // find the existing assets or groups in the children and also this will include de phantom node.
      const existingItems = props.children.filter(({props: {isLoader}, key}) => {
        // filter loader
        return !isLoader;
      });
      // insert asset in treeNode
      pos = generateItemKey(existingItems, key);
      const itemNode = createSingleNode(createAssetParam(item, pos));
      // update the children in a proper order
      props.children = [...existingItems, itemNode, loader];
      treeNode.props = props;
      return {treeNode, pos};
    };
    const {children} = props || {};
    // if current node contains sub groups
    if (children?.length) {
      // loop through all children to run this function recursively
      for (let i = 0; i < children.length; i++) {
        const {treeNode, pos: found} = locateItemIntoTree(children[i], item, pos);
        // if returned value has key, update the current children with updated
        if (found) {
          children[i] = treeNode;
          pos = found;
          break;
        };
      };
    };
  };
  return {treeNode, pos};
};

export const addItemToTree = (_treeNode, item) => {
  const nodes = isArray(_treeNode) ? _treeNode[0] : _treeNode;
  const treeClone = cloneDeep(nodes);
  const {treeNode, pos} = locateItemIntoTree(treeClone, item);
  // Object.freeze is required since React component doesn't allow object to be extensible
  return {treeNode: Object.freeze(treeNode), itemKey: pos};
};
